import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('archivos_trans_token'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem('archivos_trans_token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('archivos_trans_token', t);
    setToken(t);
    setUser(u);
    return u;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    const { token: t, user: u } = res.data;
    localStorage.setItem('archivos_trans_token', t);
    setToken(t);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem('archivos_trans_token');
    setToken(null);
    setUser(null);
  };

  const hasRole = (role) => user?.roles?.includes(role) || false;
  const isAdmin = () => hasRole('Admin');
  const isEditor = () => hasRole('Editor');
  const isReviewer = () => hasRole('Revisor');

  const getSubscriptionStatus = () => {
    if (!user) return 'none';
    if (isAdmin() || isEditor()) return 'active';
    const status = user.subscription_status || 'trial';
    if (status === 'trial') {
      const trialEnd = user.trial_end_date;
      if (trialEnd && new Date(trialEnd) < new Date()) return 'expired';
    }
    return status;
  };

  const getTrialDaysRemaining = () => {
    if (!user?.trial_end_date) return 0;
    const end = new Date(user.trial_end_date);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const canDownload = () => {
    const status = getSubscriptionStatus();
    return status === 'active' || status === 'trial';
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout,
      hasRole, isAdmin, isEditor, isReviewer, fetchUser,
      getSubscriptionStatus, getTrialDaysRemaining, canDownload
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
